import type { CallParticipantState, CallSummary } from "./api";

export type StartedCallDisposition =
  | "join"
  | "incoming"
  | "outgoing"
  | "unavailable";

type StartedCallState = Pick<CallSummary, "status" | "participant_state">;

/**
 * A start request may return the already-open call for the DM. Classifying
 * that response explicitly prevents a stale/left participant from being
 * presented as if a second outgoing call had been created.
 */
export function startedCallDisposition(
  call: StartedCallState,
): StartedCallDisposition {
  if (call.status === "active" && call.participant_state === "accepted") {
    return "join";
  }
  if (
    (call.status === "ringing" || call.status === "active")
    && call.participant_state === "ringing"
  ) {
    return "incoming";
  }
  if (call.status === "ringing" && call.participant_state === "calling") {
    return "outgoing";
  }
  return "unavailable";
}

export function hasOpenLocalCall(
  calls: ReadonlyArray<Pick<CallSummary, "status" | "participant_state"> | null>,
): boolean {
  return calls.some((call) => {
    if (call === null) return false;
    const state: CallParticipantState = call.participant_state;
    return (call.status === "ringing" || call.status === "active")
      && (state === "calling" || state === "ringing" || state === "accepted");
  });
}

interface AnswerCallWhileRequestingPermissionsOptions<T> {
  requestPermissions: () => Promise<void>;
  acceptOnServer: () => Promise<T>;
  onAccepted: (accepted: T) => void;
  rollbackAcceptedCall: (accepted: T) => Promise<void>;
}

/**
 * Start the browser permission prompt and the server-side answer in the same
 * user action. Waiting for permissions before answering can let the ringing
 * timeout expire while the browser prompt is still open. Once the server has
 * accepted, `onAccepted` runs immediately so the live UI and heartbeat can
 * advance even if the user is still choosing a microphone or camera.
 */
export async function answerCallWhileRequestingPermissions<T>({
  requestPermissions,
  acceptOnServer,
  onAccepted,
  rollbackAcceptedCall,
}: AnswerCallWhileRequestingPermissionsOptions<T>): Promise<T> {
  // Keep getUserMedia directly attached to the click before starting any
  // other asynchronous work. This matters on Safari and other browsers with
  // strict user-activation rules.
  let permissionPromise: Promise<void>;
  try {
    permissionPromise = requestPermissions();
  } catch (cause) {
    permissionPromise = Promise.reject(cause);
  }

  let acceptancePromise: Promise<T>;
  try {
    acceptancePromise = acceptOnServer().then((accepted) => {
      onAccepted(accepted);
      return accepted;
    });
  } catch (cause) {
    acceptancePromise = Promise.reject(cause);
  }

  const [permission, acceptance] = await Promise.allSettled([
    permissionPromise,
    acceptancePromise,
  ]);

  if (acceptance.status === "rejected") throw acceptance.reason;
  if (permission.status === "rejected") {
    await rollbackAcceptedCall(acceptance.value).catch(() => undefined);
    throw permission.reason;
  }
  return acceptance.value;
}
